// ConvoCX unified inbox — conversation details (CON-845).
// Right pane: status, assignment, labels, and read-receipt info.

import { Button, Divider, Selector } from '@astryxdesign/core';
import type { Conversation, ConversationStatus, Label, TeamMember } from './types';

const STATUS_OPTIONS: Array<{ value: ConversationStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
];

interface DetailsProps {
  conversation: Conversation | null;
  team: TeamMember[];
  labels: Label[];
  currentUserId: string | null;
  onStatusChange: (status: ConversationStatus) => void;
  onAssign: (userId: string | null) => void;
  onToggleLabel: (label: Label) => void;
  onToggleStar: () => void;
  onToggleArchive: () => void;
}

export function ConversationDetails({
  conversation,
  team,
  labels,
  currentUserId,
  onStatusChange,
  onAssign,
  onToggleLabel,
  onToggleStar,
  onToggleArchive,
}: DetailsProps) {
  if (!conversation) {
    return <div className="ccx-inbox__details" data-testid="inbox-details" />;
  }

  const participant = (conversation.participant_data ?? {}) as { name?: string; email?: string; phone?: string };
  const isAssignedToMe = conversation.assigned_to != null && conversation.assigned_to === currentUserId;

  const crm = (conversation.participant_data ?? {}) as Record<string, unknown>;
  const crmFields = Object.entries(crm).filter(([k]) => ['company', 'company_name', 'title', 'job_title', 'timezone', 'segment'].includes(k));

  return (
    <div className="ccx-inbox__details" data-testid="inbox-details">
      <div className="ccx-details__section">
        <h4>Conversation</h4>
        <div className="ccx-details__field">
          <span className="k">Status</span>
          <Selector
            label="Status"
            isLabelHidden
            value={conversation.status}
            onChange={(v) => onStatusChange(v as ConversationStatus)}
            options={STATUS_OPTIONS}
            size="sm"
          />
        </div>
        <div className="ccx-details__field">
          <span className="k">Assign</span>
          <Selector
            label="Assignee"
            isLabelHidden
            placeholder={isAssignedToMe ? 'You' : 'Unassigned'}
            value={conversation.assigned_to ?? ''}
            hasClear
            onChange={(v) => onAssign(v === '' ? null : v)}
            options={[
              { value: '', label: 'Unassigned' },
              ...team.map((m) => ({ value: m.id, label: m.displayName })),
            ]}
            size="sm"
          />
        </div>
        <div className="ccx-details__field">
          <span className="k">Unread</span>
          <span className="v">{conversation.unread_count ?? 0}</span>
        </div>
        <div className="ccx-details__field">
          <span className="k">Read</span>
          <span className="v">{conversation.last_read_at ? 'Yes' : 'No'}</span>
        </div>
      </div>

      <Divider />

      <div className="ccx-details__section">
        <h4>Labels</h4>
        {labels.length === 0 ? (
          <div className="ccx-details__empty">No labels in this workspace yet.</div>
        ) : (
          <div className="ccx-details__tags" data-testid="details-tags">
            {labels.map((l) => {
              const active = (conversation.labels ?? []).some((cl) => cl.id === l.id);
              return (
                <span key={l.id} className={`ccx-tag${active ? ' is-active' : ''}`} style={{ background: active ? l.color || 'var(--color-accent, #7c5cfc)' : undefined }}>
                  <span className="ccx-tag__dot" style={{ background: l.color || 'var(--color-accent, #7c5cfc)' }} />
                  {l.name}
                  <Button
                    label={active ? `Remove label ${l.name}` : `Add label ${l.name}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => onToggleLabel(l)}
                    data-testid={`label-${l.id}`}
                  >
                    {active ? '✕' : '+'}
                  </Button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      <Divider />

      <div className="ccx-details__section" data-testid="inbox-details-crm">
        <h4>Context</h4>
        {crmFields.length === 0 ? (
          <div className="ccx-details__empty">
            No CRM record linked to this conversation. Connect a contact to see account, title, and lifecycle context here.
          </div>
        ) : (
          crmFields.map(([k, v]) => (
            <div className="ccx-details__field" key={k}>
              <span className="k">{k.replace(/_/g, ' ')}</span>
              <span className="v">{String(v)}</span>
            </div>
          ))
        )}
      </div>

      <Divider />

      <div className="ccx-details__section">
        <h4>Actions</h4>
        <div className="ccx-details__field">
          <span className="k">Star</span>
          <Button
            label={conversation.is_starred ? 'Unstar' : 'Star'}
            variant={conversation.is_starred ? 'primary' : 'ghost'}
            size="sm"
            onClick={onToggleStar}
          />
        </div>
        <div className="ccx-details__field">
          <span className="k">Archive</span>
          <Button
            label={conversation.is_archived ? 'Unarchive' : 'Archive'}
            variant="ghost"
            size="sm"
            onClick={onToggleArchive}
          />
        </div>
      </div>

      <div className="ccx-details__section">
        <h4>Contact</h4>
        {participant.name && (
          <div className="ccx-details__field">
            <span className="k">Name</span>
            <span className="v">{participant.name}</span>
          </div>
        )}
        {participant.email && (
          <div className="ccx-details__field">
            <span className="k">Email</span>
            <span className="v">{participant.email}</span>
          </div>
        )}
        {participant.phone && (
          <div className="ccx-details__field">
            <span className="k">Phone</span>
            <span className="v">{participant.phone}</span>
          </div>
        )}
        <div className="ccx-details__field">
          <span className="k">Channel</span>
          <span className="v">{conversation.channel_name || conversation.channel_platform || '—'}</span>
        </div>
        <div className="ccx-details__field">
          <span className="k">Created</span>
          <span className="v">{new Date(conversation.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}